import { FaCheckCircle, FaExclamationTriangle, FaClock } from "react-icons/fa";
import { Button, Motion } from "components/common";
import { cn } from "utils/cn";
import type { CheckoutStatusPanelProps, CheckoutStatusVariant } from "./CheckoutStatusPanel.types";

const VARIANT_STYLES: Record<
  CheckoutStatusVariant,
  { iconWrap: string; icon: string; Icon: typeof FaCheckCircle }
> = {
  success: {
    iconWrap: "bg-success/10 border-success/20",
    icon: "text-success",
    Icon: FaCheckCircle,
  },
  pending: {
    iconWrap: "bg-primary/10 border-primary/20",
    icon: "text-primary",
    Icon: FaClock,
  },
  error: {
    iconWrap: "bg-danger/10 border-danger/20",
    icon: "text-danger",
    Icon: FaExclamationTriangle,
  },
};

export const CheckoutStatusPanel: React.FC<CheckoutStatusPanelProps> = ({
  variant,
  title,
  description,
  primaryAction,
  secondaryAction,
  footer,
}) => {
  const { iconWrap, icon, Icon } = VARIANT_STYLES[variant];

  return (
    <div className="w-full max-w-md mx-auto px-4 flex flex-col items-center text-center">
      <Motion type="div" property="y" className="flex flex-col items-center gap-5 w-full">
        <div
          className={cn("w-16 h-16 rounded-full border flex items-center justify-center", iconWrap)}
          aria-hidden
        >
          <Icon className={cn("text-2xl", icon)} />
        </div>

        <div className="flex flex-col gap-2 w-full">
          <h1 className="text-2xl sm:text-3xl font-semibold text-foreground tracking-tight">
            {title}
          </h1>
          <p className="text-sm sm:text-base text-muted-foreground leading-relaxed">
            {description}
          </p>
        </div>
      </Motion>

      <Motion type="div" property="y" delayValue={0.12} className="mt-8 w-full flex flex-col gap-3">
        <Button
          type="button"
          variant="badge"
          color="brand"
          size="lg"
          className="w-full"
          onClick={primaryAction.onClick}
          disabled={primaryAction.disabled}
        >
          {primaryAction.label}
        </Button>

        {secondaryAction ? (
          <Button
            type="button"
            variant="ghostSecondary"
            size="lg"
            className="w-full"
            onClick={secondaryAction.onClick}
            disabled={secondaryAction.disabled}
          >
            {secondaryAction.label}
          </Button>
        ) : null}
      </Motion>

      {footer ? (
        <Motion type="div" property="y" delayValue={0.2} className="mt-6 w-full">
          {footer}
        </Motion>
      ) : null}
    </div>
  );
};
